<template>
  <div class="admin-list-page reports-home">
    <div class="admin-page-heading mb-5">
      <div>
        <div class="text-overline primary--text font-weight-bold">Insights</div>
        <h1 class="text-h4 font-weight-bold mb-1">Reports</h1>
        <p class="admin-muted mb-0">Review sales, workforce activity, and expenses across your business.</p>
      </div>
      <v-btn outlined rounded color="primary" :to="{ name: 'location-dashboard' }">
        <v-icon left>mdi-store-outline</v-icon>View locations
      </v-btn>
    </div>

    <v-card outlined class="admin-panel rounded-xl">
      <v-card-text class="pa-4 pa-sm-6">
        <div class="reports-home__intro mb-6">
          <div>
            <h2 class="text-h6 font-weight-bold mb-1">Available reports</h2>
            <p class="text-body-2 admin-muted mb-0">Choose a report to open its existing filters and analysis tools.</p>
          </div>
          <v-chip outlined color="primary" class="font-weight-medium">{{ filteredReports.length }} reports</v-chip>
        </div>

        <v-text-field
          v-model="search"
          aria-label="Search reports"
          autocomplete="off"
          clearable dense outlined rounded hide-details
          prepend-inner-icon="mdi-magnify"
          placeholder="Search reports"
          class="admin-list-search mb-6"
        />

        <v-row v-if="filteredReports.length" align="stretch">
          <v-col v-for="report in filteredReports" :key="report.name" cols="12" sm="6" lg="4">
            <v-card
              outlined
              class="report-home-card rounded-xl"
              height="100%"
              :to="{ name: report.name }"
              :aria-label="`Open ${report.title}`"
            >
              <v-card-text class="pa-5">
                <div class="d-flex align-start">
                  <v-avatar color="primary" size="48" class="report-home-card__icon mr-4">
                    <v-icon dark>{{ report.icon }}</v-icon>
                  </v-avatar>
                  <div class="min-width-0 flex-grow-1">
                    <div class="text-subtitle-1 font-weight-bold" v-html="$options.filters.highlight(report.title, search)" />
                    <div class="text-body-2 admin-muted mt-1">{{ report.description }}</div>
                  </div>
                </div>
                <div class="report-home-card__features mt-5" role="list" :aria-label="`${report.title} capabilities`">
                  <span v-for="feature in report.features" :key="feature" class="report-home-card__feature" role="listitem">
                    <v-icon x-small color="primary" class="mr-1">mdi-check-circle-outline</v-icon>{{ feature }}
                  </span>
                </div>
              </v-card-text>
              <v-spacer />
              <v-card-actions class="px-5 pb-4 pt-0">
                <span class="primary--text font-weight-medium text-body-2">
                  Open report<v-icon color="primary" right small>mdi-chevron-right</v-icon>
                </span>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <div v-else class="admin-empty-state py-12 text-center">
          <v-icon size="56" color="primary">mdi-file-search-outline</v-icon>
          <h2 class="text-h6 mt-3">No reports found</h2>
          <p class="admin-muted mb-4">Try a different report name or keyword.</p>
          <v-btn outlined rounded color="primary" @click="search = ''">Clear search</v-btn>
        </div>

        <v-alert text color="primary" icon="mdi-information-outline" class="rounded-lg mt-6 mb-0">
          Need information for one store? Open that location’s profile for its existing store-specific reports.
        </v-alert>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "reports-home",
  data() {
    return {
      search: "",
      reports: [
        {
          name: "reports-sales",
          title: "Sales summary",
          description: "Review sales totals, trends, and breakdowns across locations and employees.",
          icon: "mdi-chart-line",
          features: ["By location", "By employee", "Date range"],
          keywords: ["revenue", "profit", "location", "employee"],
        },
        {
          name: "reports-timeClock",
          title: "Time clock summary",
          description: "Choose a location to review employee hours, shifts, and attendance.",
          icon: "mdi-clock-check-outline",
          features: ["Hours", "Attendance", "By location"],
          keywords: ["time", "hours", "shift", "attendance", "employee"],
        },
        {
          name: "reports-expenses",
          title: "Expense summary",
          description: "Review recorded business expenses and totals for a selected date range.",
          icon: "mdi-receipt-text-outline",
          features: ["Date range", "Expense totals"],
          keywords: ["expense", "cost", "spending", "date"],
        },
      ],
    };
  },
  computed: {
    filteredReports() {
      const query = String(this.search || "").trim().toLowerCase();
      if (!query) return this.reports;
      return this.reports.filter((report) => [report.title, report.description]
        .concat(report.features, report.keywords)
        .some((value) => String(value).toLowerCase().includes(query)));
    },
  },
};
</script>

<style scoped>
.reports-home__intro { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.report-home-card { cursor: pointer; display: flex; flex-direction: column; transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease; }
.report-home-card:hover,
.report-home-card:focus { border-color: var(--brand-primary); box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08); outline: none; transform: translateY(-2px); }
.report-home-card__features { display: flex; flex-wrap: wrap; gap: 8px 14px; }
.report-home-card__feature { align-items: center; display: inline-flex; font-size: 0.75rem; }
@media (max-width: 599px) {
  .reports-home__intro { align-items: flex-start; flex-direction: column; }
  .report-home-card { min-height: 220px; }
}
</style>
